import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  IAddress,
  useCompanyAddressesQuery,
  useDeleteAddressMutation
} from '../../../../generated/graphql';
import { canUserEdit } from '../../../../utils/rightUtils';
import offline from '../../../../assets/images/actions/offline.svg';
import online from '../../../../assets/images/actions/online.svg';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import { Box } from '@mui/material';
import PaperCard from '../../../Common/Layout/PaperCard';
import DenseTable from '../../../Common/Tables/DenseTable';
import AddressDetails from './AddressDetails';
import NewAddress from './NewAddress';

interface IProps {
  companyId: number;
}

const CompanyAddressesTabs: React.FunctionComponent<IProps> = ({ companyId }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>(null);
  const [openNewAddress, setOpenNewAddress] = useState(false);
  const { data, loading } = useCompanyAddressesQuery({
    variables: {
      company_id: companyId
    },
    fetchPolicy: 'no-cache'
  });
  const [deleteAddress] = useDeleteAddressMutation();
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const canCreate = canUserEdit(state.user, Types.Company);

  useEffect(() => {
    if (data && data.result) {
      setAddresses(data.result as IAddress[]);
    }
  }, [data]);

  const navStatus = (rowData: IAddress) => (
    <img className="m-auto" src={rowData.is_nav ? online : offline} alt="navStatus" />
  );

  const isMainStatus = (rowData: IAddress) => (
    <img className="m-auto" src={rowData.is_main ? online : offline} alt="mainStatus" />
  );

  const handleDetails = (rowData: IAddress) => {
    setIsEditMode(true);
    setSelectedAddress(rowData);
  };

  const handleClose = () => {
    setIsEditMode(false);
  };

  const handleRemove = (rowData: IAddress) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name}?`,
        agreeAction: handleDelete,
        params: rowData.id
      }
    });
  };

  const handleDelete = (id: string) => {
    deleteAddress({
      variables: { id: parseInt(id) },
      update(_, { data }) {
        if (data?.result) {
          const deletedAddress = addresses?.find((a) => a?.id === id);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Successfully deleted ${deletedAddress?.name}`,
              autoHide: 4000
            }
          });
          const filteredAddresses = addresses?.filter((a) => a?.id !== id);
          setAddresses(filteredAddresses);
        }
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
    dispatch({ type: Actions.HideConfirmation });
  };

  const handleAddAdress = (address: IAddress) => {
    setAddresses([address, ...(addresses || [])]);
  };

  const handleEdit = (editedAddress: IAddress) => {
    setAddresses(addresses?.map((a) => (a?.id === editedAddress.id ? editedAddress : a)));
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 65
    },
    {
      title: 'Nav',
      field: 'is_nav',
      sorting: false,
      render: navStatus,
      width: 50,
      type: 'boolean'
    },
    {
      title: 'Main',
      field: 'is_main',
      sorting: false,
      render: isMainStatus,
      width: 50,
      type: 'boolean'
    },
    {
      title: 'Address Name',
      field: 'name',
      width: 300
    },
    {
      title: 'Street',
      field: 'street',
      width: 131
    },
    {
      title: 'City',
      field: 'city',
      width: 90
    },
    {
      title: 'Country',
      field: 'country.name',
      width: 100
    },
    {
      title: 'Region',
      field: 'country.region.name',
      width: 95
    },
    {
      title: 'VAT',
      field: 'vat_number',
      width: 125
    },
    {
      title: 'Phone',
      field: 'phone',
      width: 125
    },
    {
      title: 'Email',
      field: 'email',
      width: 180
    },
    {
      title: 'Remove',
      sorting: false,
      width: 80
    }
  ] as IHeadCell[];

  return (
    <Box className="centralize" sx={{ width: '100%', paddingTop: '80px' }}>
      {isEditMode ? (
        <AddressDetails
          handleClose={handleClose}
          address={selectedAddress as IAddress}
          handleEdit={handleEdit}
        />
      ) : (
        <PaperCard
          headerText="Addresses"
          width="82%"
          additionalStyle={{ height: '680px' }}
          createButtonProps={{
            id: 'Add-Address-Button',
            onClick: () => setOpenNewAddress(true),
            text: '+ ADD ADDRESS',
            disabled: !canCreate || loading
          }}
        >
          <DenseTable
            columns={columns}
            data={addresses}
            loading={loading}
            handleDetails={handleDetails}
            handleRemove={handleRemove}
            pageSize={10}
            crudType={Types.Company}
          />
          {openNewAddress && (
            <NewAddress
              handleClose={() => setOpenNewAddress(false)}
              updateList={handleAddAdress}
              companyId={companyId}
            />
          )}
        </PaperCard>
      )}
    </Box>
  );
};

export default CompanyAddressesTabs;
